import {useMemo, useEffect, useState} from 'react'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {useQuery, UseQueryResult} from '@tanstack/react-query'
import {PageIdType, CategoryType, ProductSearchType, PageDesignerType, ProductSearchHits} from '_types'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {API} from '_customHooks'

type CustomSdkData = {
    pageIdData: PageIdType
    categoryData: CategoryType
    productData: ProductSearchType
    pageDesignerData: PageDesignerType
}

type ProductSearchHit = NonNullable<NonNullable<ProductSearchType>['hits']>[number]

async function fetchAndMergeProducts(api, params, headers) {
    const limit = params.limit
    let totalToFetch = params.offset

    // Prima richiesta per ottenere il totale disponibile
    const firstResponse = await api.shopperSearch.productSearch({
        parameters: {...params, offset: 0, limit},
        headers,
    })

    const totalAvailable = firstResponse?.total ?? 0
    // Limita totalToFetch al massimo al totale disponibile
    totalToFetch = Math.min(totalToFetch, totalAvailable)

    let mergedHits: ProductSearchHit[] = []
    const mergedResponse: ProductSearchType = {...firstResponse}

    const productsFetched = firstResponse?.hits?.length ?? 0

    if (firstResponse?.hits) mergedHits = [...firstResponse.hits]

    const remainingToFetch = totalToFetch - productsFetched
    if (remainingToFetch > 0) {
        const parallelRequests: Array<Promise<ProductSearchType>> = []
        let currentOffset = productsFetched

        while (currentOffset < totalToFetch) {
            const remaining = totalToFetch - currentOffset
            const currentLimit = Math.min(limit, remaining)
            parallelRequests.push(
                api.shopperSearch.productSearch({
                    parameters: {...params, offset: currentOffset, limit: currentLimit},
                    headers,
                }),
            )
            currentOffset += currentLimit
        }

        const parallelResponses = await Promise.all(parallelRequests)
        parallelResponses.forEach((response) => {
            if (response?.hits) mergedHits = [...mergedHits, ...response.hits]
        })
    }

    if (mergedResponse) mergedResponse.hits = mergedHits.slice(0, totalToFetch)

    return mergedResponse
}

const useCustomSdk = ({
    type,
    urlSegment,
    params,
    inheritedPageId,
    inheritedCategory,
    userLoadMore,
    searchMode = 'full',
    dyContext,
}): UseQueryResult<CustomSdkData> => {
    const api = useCommerceApi()
    const {locale} = useMultiSite()
    const {res} = useServerContext()
    const [allProducts, setAllProducts] = useState<ProductSearchHits>([])

    const {app, customParams} = getConfig()
    const siteId = app.commerceAPI.parameters.siteId
    const isProduction = customParams.isProduction
    const env = app.commerceAPI.parameters.organizationId

    const siteApi = useMemo(
        () =>
            new API({
                env,
                siteId,
                isProduction,
            }),
        [env, siteId, isProduction],
    )

    const aliasCountry = locale?.alias?.split('-')?.[1]?.toLowerCase()
    const {getTokenWhenReady} = useAccessToken()
    const queryKey = ['urlMapping', urlSegment, searchMode]

    let categoryData = inheritedCategory

    const sdkResponse = useQuery<CustomSdkData>(
        queryKey,
        async () => {
            const pathnameWithoutLocale = urlSegment.replace(/^\/[a-z]{2}-[a-z]{2}/, '')

            const token = await getTokenWhenReady()
            let pageIdData, pageDesignerData, productData

            const headers = {
                authorization: `Bearer ${token}`,
            }

            if (!params.isSearch) {
                const hasMatchingInheritedPageId = Boolean(
                    inheritedPageId && (inheritedPageId.urlSegment === urlSegment || (params.cgid && inheritedPageId.resourceId === params.cgid)),
                )

                if ((!inheritedPageId || !hasMatchingInheritedPageId) && !params.cgid) {
                    const urlMapResponse = await api.shopperSeo.getUrlMapping({parameters: {urlSegment: pathnameWithoutLocale}, headers: headers})

                    if (urlMapResponse) {
                        pageIdData = urlMapResponse
                        pageIdData.urlSegment = urlSegment
                    }
                } else {
                    if (inheritedPageId) {
                        pageIdData = inheritedPageId
                    } else if (params.cgid) {
                        pageIdData = {
                            resourceId: params.cgid,
                        }
                    }
                }

                if (pageIdData?.statusCode === '301') {
                    // res.redirect(301, pageIdData.c_destinationUrl)
                    res.status(pageIdData?.statusCode)
                    res.set('Location', pageIdData.c_destinationUrl)
                }

                if (pageIdData?.statusCode !== '301') {
                    if (type === 'category' || type === 'product' || type === 'pageDesigner') {
                        const resolvedCategoryId = pageIdData?.resourceId || params.cgid || inheritedCategory?.id
                        const canReuseCategory = Boolean(inheritedCategory?.id && resolvedCategoryId && inheritedCategory.id === resolvedCategoryId)

                        if (canReuseCategory) {
                            categoryData = inheritedCategory
                        } else {
                            const categoryParams: {id: string; c_template?: string} = {
                                id: pageIdData?.resourceId,
                            }
                            if (params?.c_template) {
                                categoryParams.c_template = params?.c_template
                            }

                            try {
                                const categoryResponse = await api.shopperProducts.getCategory({
                                    parameters: categoryParams,
                                    headers: headers,
                                })
                                if (params.cgid && categoryResponse?.c_canonicalUrl) {
                                    res.status(301)
                                    res.set('Location', categoryResponse?.c_canonicalUrl)
                                }
                                categoryData = categoryResponse
                            } catch (error) {
                                console.error('Error fetching category data:', error)
                                categoryData = null
                            }
                        }
                    }
                }
            }

            const isSimpleSearch = Boolean(params?.isSearch && params?.q)
            const standardPageMatchesUrl = !pageIdData?.urlSegment || pageIdData.urlSegment === urlSegment
            const standardCategoryMatchesPage = !pageIdData?.resourceId || pageIdData.resourceId === categoryData?.id
            const categoryActivation = categoryData?.c_dynamicYieldActivation
            const shouldUseDyInStandard =
                Boolean(categoryActivation) &&
                Boolean(dyContext?.hasDynamicYieldConfig) &&
                Boolean(categoryData?.id) &&
                !isSimpleSearch &&
                standardPageMatchesUrl &&
                standardCategoryMatchesPage &&
                (categoryActivation === 'DY' || (categoryActivation === 'AB' && dyContext?.abDyBucket && dyContext?.dyConsentAccepted))

            if (
                !shouldUseDyInStandard &&
                searchMode === 'full' &&
                ((pageIdData?.statusCode !== '301' && type === 'product' && categoryData) || params.isSearch)
            ) {
                let hasHiddenPrices = false
                let pageDesignerPromise, productSearchPromise
                if (params.isSearch) {
                    const customPrefRes = await siteApi.getCustomPreference('CountriesWithZeroPrices', token)
                    hasHiddenPrices = customPrefRes?.['CountriesWithZeroPrices']?.find((country) => country.toLowerCase() === aliasCountry)
                } else {
                    hasHiddenPrices = categoryData.c_countriesWithZeroPrices
                    pageDesignerPromise = api.shopperExperience.getPages({
                        parameters: {
                            categoryId: categoryData.id,
                            locale: locale?.id,
                            aspectTypeId: 'plp',
                            aspectAttributes: `{"category": "${categoryData.id}"}`,
                        },
                        headers: headers,
                    })
                }
                if (hasHiddenPrices && params) {
                    if (params.refine) {
                        params.refine = params.refine.filter((filter) => !filter.startsWith('price='))
                    }
                }

                const limit = params.c_template === 'search/grid3Template' ? 21 : 20

                params.limit = categoryData?.c_pageSize ? categoryData?.c_pageSize : limit

                if (params.sz <= params.limit) {
                    params.offset = 0
                    setAllProducts([])
                }
                params.expand = 'prices'

                if (!params.isSearch && pageIdData?.resourceId) {
                    params?.refine?.push(`cgid=${pageIdData?.resourceId}`)
                } else if (!params.isSearch && userLoadMore.current) {
                    params?.refine?.push(`cgid=${categoryData?.id}`)
                }
                // Check if params.refine includes a string that starts with 'pmid='
                const hasPromotionFilterExplicitlyAdded = params.refine?.some((filter) => filter.startsWith('pmid=')) || params.isSearch
                if (!hasPromotionFilterExplicitlyAdded) {
                    const activePromo = categoryData?.c_promoNosale || categoryData?.c_promoNopreview
                    if (activePromo) {
                        params.refine.push(`pmid=${activePromo}`)
                    }
                } else {
                    if (params.isSearch && categoryData?.c_promoNopreview) {
                        params.refine.push(`pmid=${categoryData?.c_promoNopreview}`)
                    }
                }

                try {
                    if (params.offset > 0 && !userLoadMore.current) {
                        productSearchPromise = fetchAndMergeProducts(api, params, headers)
                    } else {
                        if (params.offset > params.limit) {
                            params.offset = params.offset - params.limit
                        }
                        productSearchPromise = api.shopperSearch.productSearch({
                            parameters: {...params},
                            headers: headers,
                        })
                        if (userLoadMore?.current) {
                            userLoadMore.current = false
                        }
                    }

                    let pageDesignerResponse, productResponse
                    if (pageDesignerPromise) {
                        ;[pageDesignerResponse, productResponse] = await Promise.all([pageDesignerPromise, productSearchPromise])
                        pageDesignerData = pageDesignerResponse?.data[0]?.regions
                    } else {
                        productResponse = await Promise.resolve(productSearchPromise)
                    }

                    if (allProducts && allProducts?.length > 0 && productResponse?.hits && params.sz > params.limit) {
                        const prevIds = new Set(allProducts?.map((p) => p?.productId))
                        const filteredHits = productResponse.hits.filter((p) => !prevIds.has(p.productId))
                        productResponse.hits = [...allProducts, ...filteredHits]
                    }

                    productData = productResponse
                } catch (error) {
                    if (pageDesignerPromise) {
                        pageDesignerData = null
                        console.error('Error fetching product data or page designer data:', error)
                    } else {
                        console.error('Error fetching product data:', error)
                    }
                    productData = null
                    throw error
                }

                if (productData?.searchPhraseSuggestions?.c_searchRedirectStatus === 301) {
                    // res.redirect(301, productData?.searchPhraseSuggestions?.c_searchRedirectUrl)
                    res.status(productData?.searchPhraseSuggestions?.c_searchRedirectStatus)
                    res.set('Location', productData?.searchPhraseSuggestions?.c_searchRedirectUrl)
                }
            }

            return {pageIdData, categoryData, productData, pageDesignerData}
        },
        {
            staleTime: 1000 * 10,
            cacheTime: 1000 * 60 * 3,
            keepPreviousData: true,
        },
    )

    useEffect(() => {
        if (sdkResponse.data?.productData?.hits) {
            const newHits = sdkResponse.data.productData.hits
            setAllProducts((prev) => {
                if (params.offset > 0) {
                    const safePrev = prev ?? []
                    const prevIds = new Set(safePrev.map((p) => p?.productId))
                    const filteredHits = newHits.filter((p) => !prevIds.has(p.productId))
                    return [...(Array.isArray(prev) ? prev : []), ...(Array.isArray(filteredHits) ? filteredHits : [])]
                } else {
                    return newHits
                }
            })
        }
    }, [sdkResponse.data?.productData?.hits, params.offset])

    return sdkResponse
}

export {useCustomSdk}
